import { Suspense } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { AdminOrganizationsScreen } from '@/components/admin/admin-organizations-screen'
import { ViewBar } from '@/components/shell/view-bar'
import { RowsSkeleton, ViewBarSkeleton } from '@/components/shell/screen-skeletons'
import { Skeleton } from '@/components/ui/skeleton'
import { adminOrganizationsQuery } from '@/lib/queries/admin'

/**
 * Platform administration — the organizations directory.
 *
 * Lives under /o/$org so administrators keep the shell (and their bearings)
 * while looking across the platform. The org in the URL scopes nothing here;
 * it's just where they were standing when they opened it.
 *
 * No admin check on the route, matching the legacy app: the IAM policies answer
 * this query with only what the caller may see, so a non-administrator gets
 * their own organizations — the directory is a *view*, not a privilege. The
 * sidebar only shows the link to administrators.
 */
export const Route = createFileRoute('/o/$org/admin/')({
  loader: ({ context: { queryClient } }) => {
    /* Not awaited — the query resolves into the cache the screen suspends on,
       the same pattern the org route uses for the workspace queries.

       `prefetchInfiniteQuery`, and only the FIRST page: the screen pulls the
       rest as you scroll, and prefetching more here would block the transition
       on rows nobody may look at. The empty term must match the screen's
       initial one, or the component reads a different query key and refetches
       immediately. */
    void queryClient.prefetchInfiniteQuery(adminOrganizationsQuery(''))
  },

  pendingComponent: () => (
    <>
      <ViewBarSkeleton />
      <RowsSkeleton />
    </>
  ),

  component: AdminOrganizationsRoute,
})

function AdminOrganizationsRoute() {
  return (
    <>
      <ViewBar
        icon={<ShieldCheck />}
        crumbs={[{ label: 'Platform' }]}
        title="All Organizations"
      />
      <Suspense fallback={<DirectorySkeleton />}>
        <AdminOrganizationsScreen />
      </Suspense>
    </>
  )
}

function DirectorySkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
      <Skeleton className="h-5 w-96 max-w-full" />
      <Skeleton className="h-9 w-full max-w-sm" />
      <div className="border-border divide-border shrink-0 divide-y overflow-hidden rounded-xl border">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <Skeleton className="size-8 shrink-0 rounded-lg" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-48 max-w-full" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
